import { isFr } from '../i18n/lang';
// Money is exchanged with the API as decimal strings ("125.40"). All arithmetic
// here runs on integer cents (BigInt) — never on JS floats.

/** Decimal string from the API ("125.4", "-1020", "0.005") → cents, rounded half away from zero. */
export function toCents(value: string | null | undefined): bigint {
  const s = String(value ?? '').trim();
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!s || !m) return 0n;
  const [, neg, int = '', frac = ''] = m;
  const f = (frac + '000').slice(0, 3);
  let c = BigInt(int || '0') * 100n + BigInt(f.slice(0, 2));
  if (Number(f[2]) >= 5) c += 1n;
  return neg ? -c : c;
}

/** Cents → API decimal string ("1842.60"). */
export function toDecimal(cents: bigint): string {
  const neg = cents < 0n, a = neg ? -cents : cents;
  return (neg ? '-' : '') + (a / 100n).toString() + '.' + (a % 100n).toString().padStart(2, '0');
}

/**
 * User input ("1.842,60", "1842.6", "1 842,60 €", "12,") → API decimal string, or undefined when it isn't a valid
 * amount. Mirrors the design's parsing rules: with a comma present, dots are thousand separators; without a comma,
 * dots in groups of three ("1.234", "12.345.678") are thousand separators too, otherwise the dot is the decimal point.
 * A trailing separator ("12,") counts as whole euros. More than two decimals is not an amount (no silent rounding).
 */
export function parseUserAmount(input: unknown): string | undefined {
  const c = String(input ?? '').trim().replace(/\s/g, '').replace(/[^\d,.-]/g, '');
  if (!c) return undefined;
  const norm = c.includes(',') ? c.replace(/\./g, '').replace(',', '.') : /^-?\d{1,3}(\.\d{3})+$/.test(c) ? c.replace(/\./g, '') : c;
  const m = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(norm);
  if (!m || (!m[2] && !m[3])) return undefined;
  return toDecimal(toCents(m[1] + (m[2] || '0') + '.' + (m[3] ?? '')));
}

/** Cents → "1.842,60 €" (the design's de-DE format). */
/** „9.068,29 €“ in German, „9 068,29 €“ in French (narrow no-break space like RimoPrint/RimoPortail; display only, the API stays decimal). */
export function formatEur(cents: bigint): string {
  const neg = cents < 0n, a = neg ? -cents : cents;
  const int = (a / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, isFr() ? '\u202F' : '.');
  return (neg ? '-' : '') + int + ',' + (a % 100n).toString().padStart(2, '0') + ' €';
}

export const sumCents = (xs: bigint[]) => xs.reduce((a, b) => a + b, 0n);

/** Percentage discount on cents, e.g. applyDiscount(10000n, "2") → 9800n. */
export function applyDiscount(cents: bigint, ratePercent: string | number): bigint {
  const rate = toCents(String(ratePercent)); // 2 % → 200 "cents of a percent"
  const off = (cents * rate + 5000n) / 10000n;
  return cents - off;
}
